/**
 * EEHA Client Context
 *
 * Provides context for client selection and permissions across EEHA components.
 * Platform admins can select any client, regular users see only their assigned clients.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import {
  getMyPermissions,
  getClientSiteHierarchy,
  type EEHAClientPermission,
  type EEHAUserPermissions,
} from '../../../services/eehaApi';

// ============================================================================
// Types
// ============================================================================

export type PermissionLevel = 'readonly' | 'edit' | 'admin';

export interface ClientInfo {
  name: string;
  permission: PermissionLevel;
  sites: Array<{ name: string; plants?: string[] }>;
}

export interface EEHAClientContextValue {
  // Current user permissions
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;

  // Available clients for this user
  clients: ClientInfo[];

  // Selected client/site/plant (for filtering across EEHA)
  selectedClient: string | null;
  selectedSite: string | null;
  selectedPlant: string | null;
  setSelectedClient: (client: string | null) => void;
  setSelectedSite: (site: string | null) => void;
  setSelectedPlant: (plant: string | null) => void;

  // Helper to get available sites/plants based on current selection
  availableSites: string[];
  availablePlants: string[];

  // Permission check helpers
  canRead: (client?: string) => boolean;
  canEdit: (client?: string) => boolean;
  canConfigure: (client?: string) => boolean;
  getPermissionLevel: (client?: string) => PermissionLevel | null;

  // Refresh function
  refresh: () => Promise<void>;
}

// ============================================================================
// Context
// ============================================================================

const EEHAClientContext = createContext<EEHAClientContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface EEHAClientProviderProps {
  children: ReactNode;
}

const STORAGE_KEY_CLIENT = 'eeha_selected_client';
const STORAGE_KEY_SITE = 'eeha_selected_site';
const STORAGE_KEY_PLANT = 'eeha_selected_plant';

export function EEHAClientProvider({ children }: EEHAClientProviderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<EEHAUserPermissions | null>(null);
  const [clientHierarchy, setClientHierarchy] = useState<
    Array<{ client: string; sites: Array<{ name: string; plants?: string[] }> }>
  >([]);

  // Initialize from sessionStorage
  const [selectedClient, setSelectedClientState] = useState<string | null>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_CLIENT);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [selectedSite, setSelectedSiteState] = useState<string | null>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_SITE);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [selectedPlant, setSelectedPlantState] = useState<string | null>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_PLANT);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Wrapper to persist client to sessionStorage (also clears site/plant when client changes)
  const setSelectedClient = useCallback((client: string | null) => {
    setSelectedClientState(client);
    setSelectedSiteState(null);
    setSelectedPlantState(null);
    try {
      if (client === null) {
        sessionStorage.removeItem(STORAGE_KEY_CLIENT);
      } else {
        sessionStorage.setItem(STORAGE_KEY_CLIENT, JSON.stringify(client));
      }
      sessionStorage.removeItem(STORAGE_KEY_SITE);
      sessionStorage.removeItem(STORAGE_KEY_PLANT);
    } catch (err) {
      console.error('[EEHAClientContext] Failed to persist selection:', err);
    }
  }, []);

  // Wrapper to persist site to sessionStorage (also clears plant when site changes)
  const setSelectedSite = useCallback((site: string | null) => {
    setSelectedSiteState(site);
    setSelectedPlantState(null);
    try {
      if (site === null) {
        sessionStorage.removeItem(STORAGE_KEY_SITE);
      } else {
        sessionStorage.setItem(STORAGE_KEY_SITE, JSON.stringify(site));
      }
      sessionStorage.removeItem(STORAGE_KEY_PLANT);
    } catch (err) {
      console.error('[EEHAClientContext] Failed to persist site selection:', err);
    }
  }, []);

  // Wrapper to persist plant to sessionStorage
  const setSelectedPlant = useCallback((plant: string | null) => {
    setSelectedPlantState(plant);
    try {
      if (plant === null) {
        sessionStorage.removeItem(STORAGE_KEY_PLANT);
      } else {
        sessionStorage.setItem(STORAGE_KEY_PLANT, JSON.stringify(plant));
      }
    } catch (err) {
      console.error('[EEHAClientContext] Failed to persist plant selection:', err);
    }
  }, []);

  // Load permissions and client hierarchy
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [perms, hierarchy] = await Promise.all([
        getMyPermissions(),
        getClientSiteHierarchy(),
      ]);

      setPermissions(perms);
      setClientHierarchy(hierarchy);

      // Check if we have a stored selection that's still valid
      const storedClient = sessionStorage.getItem(STORAGE_KEY_CLIENT);
      const parsedStoredClient = storedClient ? JSON.parse(storedClient) : null;

      // Validate stored client is still accessible
      const validClients = perms.isAdmin
        ? hierarchy.map(h => h.client)
        : perms.clients.map(c => c.name);

      if (parsedStoredClient && validClients.some(c => c.toLowerCase() === parsedStoredClient.toLowerCase())) {
        // Keep the stored client selection - resolve to config display-case name
        const clientData = hierarchy.find(h => h.client.toLowerCase() === parsedStoredClient.toLowerCase());
        const resolvedClientName = clientData?.client || parsedStoredClient;

        // Fix case if stored name doesn't match config case
        if (resolvedClientName !== parsedStoredClient) {
          setSelectedClientState(resolvedClientName);
          try {
            sessionStorage.setItem(STORAGE_KEY_CLIENT, JSON.stringify(resolvedClientName));
          } catch (_e) { /* ignore */ }
        }

        // Also validate site and plant
        const validSites = clientData?.sites.map(s => s.name) || [];

        const storedSite = sessionStorage.getItem(STORAGE_KEY_SITE);
        const parsedStoredSite = storedSite ? JSON.parse(storedSite) : null;

        if (parsedStoredSite && !validSites.includes(parsedStoredSite)) {
          // Invalid site, clear it
          setSelectedSiteState(null);
          setSelectedPlantState(null);
          sessionStorage.removeItem(STORAGE_KEY_SITE);
          sessionStorage.removeItem(STORAGE_KEY_PLANT);
        } else if (parsedStoredSite) {
          // Validate plant
          const siteData = clientData?.sites.find(s => s.name === parsedStoredSite);
          const validPlants = siteData?.plants || [];

          const storedPlant = sessionStorage.getItem(STORAGE_KEY_PLANT);
          const parsedStoredPlant = storedPlant ? JSON.parse(storedPlant) : null;

          if (parsedStoredPlant && !validPlants.includes(parsedStoredPlant)) {
            // Invalid plant, clear it
            setSelectedPlantState(null);
            sessionStorage.removeItem(STORAGE_KEY_PLANT);
          }
        }
        return;
      }

      // Only auto-select if no valid stored selection
      const autoSelectClientAndSite = (clientName: string) => {
        // Resolve to config display-case name (API may return lowercase)
        const clientEntry = hierarchy.find(
          h => h.client.toLowerCase() === clientName.toLowerCase()
        );
        const resolvedName = clientEntry?.client || clientName;
        setSelectedClient(resolvedName); // This clears site/plant
        // Auto-select site if the user has exactly one site for this client
        if (clientEntry?.sites?.length === 1) {
          const siteName = clientEntry.sites[0].name;
          setSelectedSiteState(siteName);
          try {
            sessionStorage.setItem(STORAGE_KEY_SITE, JSON.stringify(siteName));
          } catch (_err) {
            // ignore
          }
          // Auto-select plant if there's exactly one plant
          if (clientEntry.sites[0].plants?.length === 1) {
            const plantName = clientEntry.sites[0].plants[0];
            setSelectedPlantState(plantName);
            try {
              sessionStorage.setItem(STORAGE_KEY_PLANT, JSON.stringify(plantName));
            } catch (_err) {
              // ignore
            }
          }
        }
      };

      if (perms.clients.length === 1) {
        autoSelectClientAndSite(perms.clients[0].name);
      } else if (perms.isAdmin) {
        // For admins, don't auto-select - let them see "All Clients" view
        setSelectedClient(null);
      } else if (perms.clients.length > 0) {
        // For non-admins with multiple clients, select first
        autoSelectClientAndSite(perms.clients[0].name);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load permissions';
      console.error('[EEHAClientContext] Load error:', message);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [setSelectedClient]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build clients list with full info
  const clients = useMemo<ClientInfo[]>(() => {
    if (!permissions) return [];

    // For admins, show all clients from hierarchy
    if (permissions.isAdmin) {
      return clientHierarchy.map(h => ({
        name: h.client,
        permission: 'admin' as PermissionLevel,
        sites: h.sites || [],
      }));
    }

    return permissions.clients.map((perm: EEHAClientPermission) => {
      const match = clientHierarchy.find(h => h.client.toLowerCase() === perm.name.toLowerCase());
      return {
        name: match?.client || perm.name,
        permission: perm.permission,
        sites: match?.sites || [],
      };
    });
  }, [permissions, clientHierarchy]);

  // Compute available sites based on selected client
  const availableSites = useMemo<string[]>(() => {
    if (!selectedClient) return [];
    const clientData = clientHierarchy.find(h => h.client.toLowerCase() === selectedClient.toLowerCase());
    return clientData?.sites.map(s => s.name) || [];
  }, [selectedClient, clientHierarchy]);

  // Compute available plants based on selected client and site
  const availablePlants = useMemo<string[]>(() => {
    if (!selectedClient || !selectedSite) return [];
    const clientData = clientHierarchy.find(h => h.client.toLowerCase() === selectedClient.toLowerCase());
    const siteData = clientData?.sites.find(s => s.name.toLowerCase() === selectedSite.toLowerCase());
    return siteData?.plants || [];
  }, [selectedClient, selectedSite, clientHierarchy]);

  // Permission check helpers
  const getPermissionLevel = useCallback(
    (client?: string): PermissionLevel | null => {
      if (!permissions) return null;

      const targetClient = client || selectedClient;

      // Admin has admin on everything
      if (permissions.isAdmin) return 'admin';

      // No client specified and none selected
      if (!targetClient) return null;

      // Find permission for this client (case-insensitive)
      const clientPerm = permissions.clients.find(c => c.name.toLowerCase() === targetClient.toLowerCase());
      return clientPerm?.permission || null;
    },
    [permissions, selectedClient]
  );

  const canRead = useCallback(
    (client?: string): boolean => {
      const level = getPermissionLevel(client);
      return level !== null;
    },
    [getPermissionLevel]
  );

  const canEdit = useCallback(
    (client?: string): boolean => {
      const level = getPermissionLevel(client);
      return level === 'edit' || level === 'admin';
    },
    [getPermissionLevel]
  );

  const canConfigure = useCallback(
    (client?: string): boolean => {
      const level = getPermissionLevel(client);
      return level === 'admin';
    },
    [getPermissionLevel]
  );

  // Context value
  const value = useMemo<EEHAClientContextValue>(
    () => ({
      isAdmin: permissions?.isAdmin || false,
      isLoading,
      error,
      clients,
      selectedClient,
      selectedSite,
      selectedPlant,
      setSelectedClient,
      setSelectedSite,
      setSelectedPlant,
      availableSites,
      availablePlants,
      canRead,
      canEdit,
      canConfigure,
      getPermissionLevel,
      refresh: loadData,
    }),
    [
      permissions?.isAdmin,
      isLoading,
      error,
      clients,
      selectedClient,
      selectedSite,
      selectedPlant,
      setSelectedClient,
      setSelectedSite,
      setSelectedPlant,
      availableSites,
      availablePlants,
      canRead,
      canEdit,
      canConfigure,
      getPermissionLevel,
      loadData,
    ]
  );

  return <EEHAClientContext.Provider value={value}>{children}</EEHAClientContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useEEHAClient(): EEHAClientContextValue {
  const context = useContext(EEHAClientContext);
  if (!context) {
    throw new Error('useEEHAClient must be used within an EEHAClientProvider');
  }
  return context;
}

export default EEHAClientContext;
